import { useState, useEffect } from 'react';

const LABEL_RECORD = {
  eventi: 'Eventi diario',
  report: 'Report giornalieri',
  documenti: 'Documenti collegati',
  preventivi: 'Preventivi',
  fatture_bozza: 'Fatture in bozza o annullate',
};

export default function ConfirmDeleteModal({ isOpen, onClose, onConfirm, preview, isLoading }) {
  const [conferma, setConferma] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setConferma('');
      return;
    }
    const onKey = (e) => { if (e.key === 'Escape' && !isLoading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen || !preview) return null;

  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  };
  const cardStyle = {
    background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)',
    width: '100%', maxWidth: 480,
    boxShadow: '0 12px 40px rgba(16,24,40,0.12)',
    padding: '32px 28px',
  };
  const titleStyle = { margin: '0 0 14px', fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--ink)' };
  const textStyle = { margin: '0 0 16px', fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6 };
  const warningBoxStyle = {
    margin: '0 0 16px', padding: '10px 14px',
    background: 'rgba(229,83,61,0.08)',
    border: '1px solid rgba(229,83,61,0.2)',
    borderRadius: 'var(--radius-base)', color: 'var(--ds-danger)',
    fontSize: 13, lineHeight: 1.6,
  };
  const btnPrimaryStyle = {
    padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none',
    background: 'var(--brand)', color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
    minHeight: 44,
  };
  const btnDangerStyle = {
    padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none',
    background: 'var(--ds-danger)', color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
    minHeight: 44,
  };
  const btnSecondaryStyle = {
    padding: '10px 20px', borderRadius: 'var(--radius-base)',
    border: '1px solid var(--ds-border)',
    background: 'var(--ds-bg)', color: 'var(--ds-text)',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
    minHeight: 44,
  };
  const footerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 };

  // CASO 1 — eliminazione bloccata da fatture con obbligo conservazione
  if (preview.can_delete === false) {
    return (
      <div style={overlayStyle} onClick={() => !isLoading && onClose()}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <h2 style={titleStyle}>Eliminazione non possibile</h2>
          <p style={textStyle}>
            {preview.message || `Non puoi eliminare questo cantiere perché ha ${preview.fatture_bloccanti || 0} fattura/e con obbligo di conservazione decennale.`}
          </p>
          <p style={warningBoxStyle}>
            Suggerimento: se il cantiere è terminato, cambia lo stato in "Completato".
          </p>
          <div style={footerStyle}>
            <button onClick={onClose} style={btnPrimaryStyle}>Chiudi</button>
          </div>
        </div>
      </div>
    );
  }

  const totale = preview.totale_record_eliminati || 0;
  const recordEntries = Object.entries(preview.record_collegati || {}).filter(([, v]) => v > 0);

  // CASO 2 — molti record collegati: doppia conferma scrivendo il nome
  if (totale >= 3) {
    const matchTitolo = conferma.trim() === preview.titolo;
    return (
      <div style={overlayStyle} onClick={() => !isLoading && onClose()}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <h2 style={titleStyle}>Elimina cantiere "{preview.titolo}"</h2>
          <p style={textStyle}>Stai per eliminare i seguenti record collegati:</p>
          <ul style={{
            margin: '0 0 16px', padding: '12px 16px 12px 32px',
            background: 'var(--chip)',
            border: '1px solid var(--ds-border)',
            borderRadius: 'var(--radius-base)',
            fontSize: 13, color: 'var(--ds-text)', lineHeight: 1.8,
          }}>
            {recordEntries.map(([k, v]) => (
              <li key={k}>
                {LABEL_RECORD[k] || k}: <strong style={{ fontWeight: 500 }}>{v}</strong>
              </li>
            ))}
          </ul>
          <p style={{ ...warningBoxStyle, fontWeight: 500 }}>
            Operazione irreversibile.
          </p>
          <label htmlFor="conferma-titolo" style={{
            display: 'block', fontSize: 12, fontWeight: 500,
            color: 'var(--ds-text)', marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Scrivi il nome del cantiere per confermare
          </label>
          <input
            id="conferma-titolo"
            type="text"
            value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            placeholder={preview.titolo}
            disabled={isLoading}
            autoFocus
            style={{
              width: '100%', padding: '10px 12px',
              borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
              fontSize: 13, marginBottom: 16,
              background: 'var(--ds-bg)', color: 'var(--ds-text)',
              boxSizing: 'border-box',
              minHeight: 44,
            }}
          />
          <div style={footerStyle}>
            <button onClick={onClose} disabled={isLoading} style={btnSecondaryStyle}>Annulla</button>
            <button
              onClick={onConfirm}
              disabled={!matchTitolo || isLoading}
              style={{
                ...btnDangerStyle,
                opacity: matchTitolo && !isLoading ? 1 : 0.5,
                cursor: matchTitolo && !isLoading ? 'pointer' : 'not-allowed',
              }}
            >
              {isLoading ? 'Eliminazione...' : 'Elimina'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CASO 3 — pochi record: conferma semplice
  return (
    <div style={overlayStyle} onClick={() => !isLoading && onClose()}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Elimina cantiere "{preview.titolo}"</h2>
        <p style={textStyle}>
          Vuoi eliminare il cantiere <strong style={{ fontWeight: 500, color: 'var(--ds-text)' }}>{preview.titolo}</strong>?
          {totale > 0 && ` Verranno eliminati ${totale} record collegati.`}
        </p>
        <div style={footerStyle}>
          <button onClick={onClose} disabled={isLoading} style={btnSecondaryStyle}>Annulla</button>
          <button onClick={onConfirm} disabled={isLoading} style={btnDangerStyle}>
            {isLoading ? 'Eliminazione...' : 'Sì, elimina'}
          </button>
        </div>
      </div>
    </div>
  );
}
